<script setup>
import { ref, computed, onMounted } from 'vue';

const PREVIEW_ROW_LIMIT = 25;

const month = ref(defaultMonth());
const report = ref(null);
const isLoading = ref(false);
const isDownloading = ref(false);
const error = ref('');

function defaultMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const monthNumber = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${monthNumber}`;
}

const previewRideRows = computed(() => {
  if (!report.value || !report.value.rideRows) return [];
  return report.value.rideRows.slice(0, PREVIEW_ROW_LIMIT);
});

function formatMinor(minor) {
  if (minor === null || minor === undefined) return 'n/a';
  return (minor / 100).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

function formatPercentage(value) {
  if (value === null || value === undefined) return 'n/a';
  return `${Number(value).toFixed(2)}%`;
}

function formatFactor(value) {
  if (value === null || value === undefined) return 'n/a';
  return Number(value).toFixed(4);
}

async function loadReport() {
  isLoading.value = true;
  error.value = '';
  try {
    const response = await fetch(`/api/admin/insurance/exposure-report?month=${encodeURIComponent(month.value)}`);
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to load the report');
    }
    report.value = await response.json();
  } catch (err) {
    error.value = err.message || 'Failed to load the report';
    report.value = null;
  } finally {
    isLoading.value = false;
  }
}

async function downloadWorkbook() {
  isDownloading.value = true;
  try {
    const response = await fetch(`/api/admin/insurance/exposure-report/export?month=${encodeURIComponent(month.value)}`);
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to download the workbook');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `northbridge-exposure-${month.value}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (err) {
    error.value = err.message || 'Failed to download the workbook';
  } finally {
    isDownloading.value = false;
  }
}

onMounted(loadReport);
</script>

<template>
  <div class="insurance-exposure-report">
    <div class="page-title">
      <div class="row">
        <div class="col-sm-6 col-12">
          <h2>Northbridge Exposure Report</h2>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-auto">
        <label for="report-month" class="form-label">Month</label>
        <input id="report-month" v-model="month" type="month" class="form-control" @change="loadReport">
      </div>
      <div class="col-auto align-self-end">
        <button class="btn btn-primary" :disabled="isLoading" @click="loadReport">
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          {{ isLoading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
      <div class="col-auto align-self-end">
        <button class="btn btn-outline-primary" :disabled="isDownloading || !report" @click="downloadWorkbook">
          <span v-if="isDownloading" class="spinner-border spinner-border-sm me-2"></span>
          {{ isDownloading ? 'Preparing...' : 'Download .xlsx workbook' }}
        </button>
      </div>
    </div>

    <div class="alert alert-secondary">
      Covering note: "Exclusive" is reported as rides with 4 or more passengers; "Taxi?" is reported
      as "No" for all vehicles.
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-else-if="report">
      <div class="row mb-4">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card stats-card">
            <div class="card-body">
              <p class="text-muted mb-1">IndividualRides rows</p>
              <h3 class="mb-0">{{ report.rideRows ? report.rideRows.length : 0 }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card stats-card">
            <div class="card-body">
              <p class="text-muted mb-1">IndividualDrivers rows</p>
              <h3 class="mb-0">{{ report.driverRows ? report.driverRows.length : 0 }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card stats-card">
            <div class="card-body">
              <p class="text-muted mb-1">Measured deadhead factor</p>
              <h3 class="mb-0">{{ formatFactor(report.measuredDeadheadFactor) }}</h3>
              <small class="text-muted">Internal metric only -- not part of the insurer filing</small>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card stats-card">
            <div class="card-body">
              <p class="text-muted mb-1">Report status</p>
              <h3 class="mb-0">
                <span v-if="report.discrepancies && report.discrepancies.length === 0" class="text-success">Clean</span>
                <span v-else class="text-warning">{{ report.discrepancies ? report.discrepancies.length : 0 }} finding(s)</span>
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-4">
        <div class="card-body">
          <h5 class="card-title">Reserve ledger reconciliation</h5>
          <p class="text-muted">
            Measured exposure (this report) against #186's accrued ledger for the same window. A delta
            is expected by design -- the ledger accrues a modelled figure while this report measures
            actuals -- so this is never treated as an equality check.
          </p>
          <div v-if="report.reconciliation" class="row">
            <div class="col-md-3 col-sm-6 mb-2">
              <p class="text-muted mb-1">Measured</p>
              <h5>{{ formatMinor(report.reconciliation.measuredAmountMinor) }}</h5>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <p class="text-muted mb-1">Accrued (ledger)</p>
              <h5>{{ formatMinor(report.reconciliation.accruedAmountMinor) }}</h5>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <p class="text-muted mb-1">Delta</p>
              <h5>{{ formatMinor(report.reconciliation.deltaMinor) }} ({{ formatPercentage(report.reconciliation.deltaPercentage) }})</h5>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <p class="text-muted mb-1">Within tolerance</p>
              <h5>
                <span v-if="report.reconciliation.withinTolerance" class="text-success">Yes</span>
                <span v-else class="text-warning">No</span>
              </h5>
            </div>
          </div>
        </div>
      </div>

      <div v-if="report.discrepancies && report.discrepancies.length" class="card mb-4">
        <div class="card-body">
          <h5 class="card-title">Findings</h5>
          <ul class="mb-0">
            <li v-for="(finding, index) in report.discrepancies" :key="index"
                :class="finding.severity === 'CRITICAL' ? 'text-danger' : 'text-warning'">
              [{{ finding.severity }}] {{ finding.detail }}
            </li>
          </ul>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <h5 class="card-title">IndividualRides preview (first {{ PREVIEW_ROW_LIMIT }} rows)</h5>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Pickup point</th>
                  <th>Make</th>
                  <th>Model</th>
                  <th>Year</th>
                  <th>Age</th>
                  <th>Taxi?</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in previewRideRows" :key="row.rideId">
                  <td>{{ row.sequentialNumber }}</td>
                  <td>{{ row.pickupAddress }}</td>
                  <td>{{ row.make }}</td>
                  <td>{{ row.model }}</td>
                  <td>{{ row.year }}</td>
                  <td>{{ row.driverAgeYears }}</td>
                  <td>{{ row.taxi }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>
</template>
